import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setToken } from './App';
import LanguageSelector from './LanguageSelector';
import { changeLanguage, getCurrentLanguage } from './i18n';
import { ThemeToggle } from './theme';
import { Ico } from './icons';


function Login() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPw, setShowPw] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch('/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.error || t('common.http_error', { status: res.status }));
                return;
            }
            setToken(data.token);
            localStorage.setItem('nexus_user', JSON.stringify(data.user));

            const userLang = data.user?.preferred_language;
            if (userLang && userLang !== getCurrentLanguage()) {
                await changeLanguage(userLang);
            }

            if (data.user?.role === 'superadmin') {
                navigate('/superadmin/tenants', { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        } catch (err) {
            setError(t('common.network_error', { message: err.message }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page">

            <section className="login-hero">
                <div className="orb orb-1" aria-hidden="true" />
                <div className="orb orb-2" aria-hidden="true" />
                <div className="orb orb-3" aria-hidden="true" />
                <div className="hero-gridlines" aria-hidden="true" />

                <div className="hero-brand">
                    <div className="brand-mark"><Ico name="logo" size={20} strokeWidth={0} /></div>
                    <span>Nexus</span>
                </div>

                <div className="hero-body">
                    <span className="hero-eyebrow">
                        <Ico name="sparkles" size={13} />
                        {t('app.tagline', 'Control horario inteligente')}
                    </span>
                    <h1 className="hero-title">
                        {t('login.hero_title_1', 'Gestiona el tiempo')}
                        <br />
                        <span className="grad">{t('login.hero_title_2', 'de tu equipo')}</span>
                        <br />
                        {t('login.hero_title_3', 'sin fricción')}
                    </h1>
                    <p className="hero-sub">
                        {t('login.hero_subtitle', 'Fichaje NFC, informes automáticos y arquitectura multi-tenant en una sola plataforma.')}
                    </p>

                    <div className="hero-chips">
                        <span className="chip"><Ico name="fingerprint" size={14} />{t('login.chip_nfc', 'Fichaje NFC')}</span>
                        <span className="chip"><Ico name="file" size={14} />{t('login.chip_reports', 'XLSX · PDF')}</span>
                        <span className="chip"><Ico name="shield" size={14} />{t('login.chip_secure', 'Aislamiento por tenant')}</span>
                    </div>
                </div>

                <div className="hero-foot">NEXUS PLATFORM · v3.0 PRIME</div>
            </section>

            <section className="login-panel">
                <div className="login-topbar">
                    <ThemeToggle />
                    <LanguageSelector />
                </div>

                <div className="login-center">
                    <div className="login-card">
                        <div className="login-logo"><Ico name="logo" size={26} strokeWidth={0} /></div>
                        <h1 className="login-title">{t('login.welcome_back', 'Bienvenido de nuevo')}</h1>
                        <p className="login-sub">
                            {t('login.subtitle', 'Entra para gestionar los fichajes de tu equipo.')}
                        </p>

                        <form onSubmit={handleSubmit} noValidate>
                            <div className="field">
                                <label className="field-label" htmlFor="login-email">
                                    {t('login.email_label')}
                                </label>
                                <div className="control">
                                    <span className="control-ico"><Ico name="mail" size={17} /></span>
                                    <input
                                        id="login-email"
                                        className="input pad-ico"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder={t('login.email_placeholder')}
                                        autoComplete="email"
                                        required
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <div className="field">
                                <label className="field-label" htmlFor="login-password">
                                    {t('login.password_label')}
                                </label>
                                <div className="control">
                                    <span className="control-ico"><Ico name="lock" size={17} /></span>
                                    <input
                                        id="login-password"
                                        className="input pad-ico pad-end"
                                        type={showPw ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder={t('login.password_placeholder')}
                                        autoComplete="current-password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="input-affix"
                                        onClick={() => setShowPw((v) => !v)}
                                        title={showPw ? t('common.hide') : t('common.show')}
                                        aria-label={showPw ? t('common.hide') : t('common.show')}
                                    >
                                        <Ico name={showPw ? 'eyeOff' : 'eye'} size={17} />
                                    </button>
                                </div>
                            </div>

                            {error && (
                                <div className="alert alert-error" role="alert">
                                    <Ico name="alert" size={17} />
                                    <span>{error}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                className="btn btn-primary login-submit"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Ico name="refresh" size={16} />
                                        {t('login.submitting')}
                                    </>
                                ) : (
                                    <>
                                        {t('login.submit')}
                                        <Ico name="arrowRight" size={16} />
                                    </>
                                )}
                            </button>
                        </form>

                        <div className="login-foot">
                            <span className="secure">
                                <Ico name="shield" size={13} />
                                {t('login.secure', 'Conexión segura')}
                            </span>
                            <span className="login-version">NEXUS PRIME v3.0</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Login;
